import { format } from 'date-fns';
import { isCommittedBooking } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { aud } from '@/utils/format';
import { arrangementForArtist, computePayout } from '@/domain/commissionEngine';
import { salesByBookingId, sessionTakingsFor } from '@/domain/sessionTakings';
import { CLOCKOFF_HOUR } from '@/domain/paymentTiming';
import { ALERT_BANDS } from '@/data/reportingTargets';
import { seedNow } from '@/domain/seedContext';
import { dayKey } from '@/utils/studioDay';
import type {
  Artist,
  Booking,
  CashLedger,
  CommissionArrangement,
  FloatMovement,
  PayoutRecord,
  StudioId,
} from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Shared payout derivation (Prompt E). ONE place computes each artist's envelope
 * for a day from the store — the E2 payout section AND the E1 steering both read
 * this, so the money can never disagree between the two screens (READ, DON'T
 * RECOMPUTE). The commission maths itself lives in `commissionEngine`; this only
 * groups a day's work per artist and applies it.
 */

export interface DayPayout {
  artistId: string;
  artistName: string;
  arrangement: CommissionArrangement;
  /** Commissionable session takings for the day (day rates, merch excluded). */
  takingsAud: number;
  sessionCount: number;
  /** The envelope the artist is owed for the day. */
  envelopeAud: number;
  /**
   * When the artist is due to be PAID: the end of their LAST SESSION of the day
   * (L5, 17 Jul - client: pay at the last session, not the rostered shift end),
   * clamped to the 6pm clock-off for evening / double-day finishes. Set once
   * `payable`. Absent while any session is still in the chair.
   */
  finishIso?: string;
  /**
   * The artist's projected last-session end: their latest actual checkout, or the
   * scheduled end of a session still running, clamped to the 6pm clock-off. Always
   * set when the artist has any booking today, so the finish timeline can place
   * everyone (this is the same instant as finishIso once payable).
   */
  expectedFinishIso?: string;
  /**
   * True when the artist's real last-session finish is past the 6pm clock-off, so
   * their payout moment was pulled forward to 6pm (H): evening or double-day
   * artists. Drives the "6pm"/"evening" tag on the payout card.
   */
  clampedToClockoff: boolean;
  /**
   * True once every one of the artist's sessions today is checked out — only
   * then does the envelope become signable (artists finish at different times).
   */
  payable: boolean;
  /**
   * True once every session's sale is settled (a SalePayment recorded at
   * checkout). Distinguishes "payable now" (all sold) from a transient
   * "sale pending" state on the artist card (L5).
   */
  saleSettled: boolean;
  /** An existing payout record for this artist + day, if one has been written. */
  record?: PayoutRecord;
  /** Already handed over (from the record). */
  paidAud: number;
  /** Still owed = envelope − paid (never negative). */
  outstandingAud: number;
}

// A payout envelope only exists for work that happened or is happening. Held /
// pending-deposit / confirmed (arriving) / rescheduled / superseded bookings are
// not sessions and never produce a payout row.
function isCommissionable(b: Booking): boolean {
  return b.status === 'in-chair' || b.status === 'completed';
}

// A committed appointment: someone booked and is expected in (or is in / was in)
// the chair. Live + solid (@atink/domain `isCommittedBooking`): excludes pre-commit
// states (held / pending-deposit) and dead rows (cancelled / no-show /
// superseded). A legacy 'rescheduled' row is a moved, booked slot and counts.
export function isCommitted(b: Booking): boolean {
  return isCommittedBooking(b);
}

/** Deterministic per-artist+day id for a payout record. */
export function payoutRecordId(studioId: StudioId, artistId: string, dayIso: string): string {
  return `payout-${studioId}-${artistId}-${dayKey(dayIso)}`;
}

/** Deterministic per-store+day id for a cash ledger. */
export function cashLedgerId(studioId: StudioId, dayIso: string): string {
  return `${studioId}:${dayKey(dayIso)}`;
}

// getDay() index (0=Sun..6=Sat) -> DayOfWeek key. Mirrors artistAdmin.DOW_BY_INDEX.
const DOW_BY_INDEX = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * The artist's ROSTERED shift end for a day (D6), not the last session's
 * checkout. Base = the weekday pattern window's end; extended to the second
 * shift's end on a two-shift day (`secondShiftDates`). Returns a Z ISO string
 * (matches dayPayouts' expectedFinish format) or undefined when not rostered.
 * ASSUMPTION: studio-local times are +10:00 (matches the finance seeds).
 */
export function resolveShiftEnd(artist: Artist, dayIso: string): string | undefined {
  const dateKey = dayKey(dayIso);
  const dow = DOW_BY_INDEX[new Date(`${dateKey}T12:00:00+10:00`).getDay()]!;
  const base = artist.availability?.pattern?.[dow];
  const second = artist.availability?.secondShiftDates?.[dateKey];
  const endTime = second ? (second.endTime > (base?.endTime ?? '') ? second.endTime : base?.endTime) : base?.endTime;
  if (!endTime) return undefined;
  return new Date(`${dateKey}T${endTime}:00+10:00`).toISOString();
}

/**
 * The moment an artist must be PAID BY on a day: their finish, but never later
 * than the 6pm clock-off. Everybody is paid by 6pm (H evening rule), so an
 * evening or double-day artist is due at 6pm, before that block starts. Feeds
 * dayPayouts ordering + the cash-cover timeline; `resolveShiftEnd` itself stays
 * the true rostered end. ASSUMPTION: studio-local times are +10:00.
 */
/** ISO instant of the 6pm clock-off for a day (studio-local +10:00). */
function clockoffIsoFor(dayIso: string): string {
  return new Date(
    `${dayKey(dayIso)}T${String(CLOCKOFF_HOUR).padStart(2, '0')}:00:00+10:00`,
  ).toISOString();
}

function clampToClockoff(iso: string | undefined, dayIso: string): string | undefined {
  if (!iso) return iso;
  const clockoff = clockoffIsoFor(dayIso);
  // Normalise to a Z-format instant either way: finish times now come from
  // booking checkouts (which may carry a +10:00 offset) as well as the Z-format
  // clock-off, and the dayPayouts / steering sorts compare these lexically.
  return new Date(new Date(iso).getTime() <= new Date(clockoff).getTime() ? iso : clockoff).toISOString();
}

/**
 * Shared derivation behind `dayPayouts` and `dayExpectedPayouts` — only the
 * eligibility gate differs between the actual (signable) and committed
 * (forward-looking) views. Everything else — grouping, commission maths,
 * finish-time resolution, sort order — is identical, so the two views can
 * never drift apart on anything but which bookings they admit.
 */
function computeDayPayouts(
  studioId: StudioId,
  dayIso: string,
  isEligible: (b: Booking) => boolean,
): DayPayout[] {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const sales = salesByBookingId(brain.salePayments);
  const byArtist = new Map<string, Booking[]>();
  for (const b of Object.values(brain.bookings)) {
    if (b.studioId !== studioId || dayKey(b.start) !== day || !isEligible(b)) continue;
    const list = byArtist.get(b.artistId) ?? [];
    list.push(b);
    byArtist.set(b.artistId, list);
  }

  const out: DayPayout[] = [];
  for (const [artistId, bookings] of byArtist) {
    const artist = brain.artists[artistId];
    if (!artist) continue;

    // B9 (75nk, Anas 1 Oct): a commission change applies to days not yet paid.
    // A day with a payout already written (sent / signed / deferred) keeps the
    // arrangement snapshotted on its record, so editing the rate never reprices it.
    const record = brain.payoutRecords[payoutRecordId(studioId, artistId, dayIso)];
    const arrangement = record?.arrangement ?? arrangementForArtist(artist);
    // B1 (8tqr): priced from the SALE, not the booking's quote — a walk-in or a
    // seeded booking with no day rate was owed $0.
    const breakdown = computePayout(
      arrangement,
      bookings.map((b) => ({
        bookingId: b.id,
        amountAud: sessionTakingsFor(b, sales, brain.artists),
      })),
    );

    const payable = bookings.every((b) => b.status === 'completed');
    // L5 (17 Jul, client): the payout is due at the artist's LAST SESSION of the
    // day — their actual last checkout, else the scheduled end of a session still
    // running — NOT their rostered shift end. `resolveShiftEnd` stays for the
    // roster read elsewhere; it no longer drives the payout-due moment.
    const rawFinish = bookings
      .map((b) =>
        new Date(
          b.checkedOutAt ?? new Date(new Date(b.start).getTime() + b.durationMin * 60_000).toISOString(),
        ).toISOString(),
      )
      .sort()
      .at(-1);
    // The pay-by moment is clamped to the 6pm clock-off (H): an evening /
    // double-day artist whose last session runs past 6pm is paid at 6pm.
    const expectedFinishIso = clampToClockoff(rawFinish, dayIso);
    const finishIso = payable ? expectedFinishIso : undefined;
    // Last-session finish past 6pm → payout pulled forward to the clock-off; the
    // card marks these "evening".
    const clampedToClockoff =
      rawFinish != null && new Date(rawFinish).getTime() > new Date(clockoffIsoFor(dayIso)).getTime();
    // Every session's sale rung up (SalePayment written at checkout, keyed
    // `sale-<bookingId>`); drives the "payable now" vs "sale pending" card state.
    const saleSettled = bookings.every((b) => !!brain.salePayments[`sale-${b.id}`]);

    const paidAud = record?.amountPaidAud ?? 0;

    out.push({
      artistId,
      artistName: artist.displayName,
      arrangement,
      takingsAud: breakdown.grossAud,
      sessionCount: breakdown.sessionCount,
      envelopeAud: breakdown.payoutAud,
      finishIso,
      expectedFinishIso,
      clampedToClockoff,
      payable,
      saleSettled,
      record,
      paidAud,
      outstandingAud: Math.max(0, breakdown.payoutAud - paidAud),
    });
  }

  // Earliest expected finisher first, so the cash has to be ready in reading
  // order; ties (e.g. everyone clamped to the 6pm clock-off) break by the
  // biggest envelope first.
  return out.sort((a, b) => {
    const byFinish = (a.expectedFinishIso ?? '~').localeCompare(b.expectedFinishIso ?? '~');
    return byFinish !== 0 ? byFinish : b.envelopeAud - a.envelopeAud;
  });
}

/**
 * Every artist working the given studio+day, with their envelope and sign state.
 * Sorted by finish time so the earliest finisher (who needs paying first) leads.
 * The ACTUAL, signable set — in-chair/completed sessions only. See
 * `dayExpectedPayouts` for the forward-looking committed set.
 */
export function dayPayouts(studioId: StudioId, dayIso: string): DayPayout[] {
  return computeDayPayouts(studioId, dayIso, isCommissionable);
}

/**
 * The day's COMMITTED appointments (confirmed / checked-in / in-chair /
 * completed) grouped by artist, each with an EXPECTED envelope — a
 * forward-looking view that also surfaces artists who are booked in but
 * haven't started yet. A row only becomes signable once every one of the
 * artist's sessions is `completed` (`payable`), same as `dayPayouts`; until
 * then it's a preview of what the day is expected to owe, not an actual
 * payout. `dayPayouts` remains the actual/signable set (in-chair/completed
 * only) and is unchanged.
 */
export function dayExpectedPayouts(studioId: StudioId, dayIso: string): DayPayout[] {
  return computeDayPayouts(studioId, dayIso, isCommitted);
}

/**
 * The EXPECTED committed payouts still due today for a studio (forward-looking):
 * expected envelopes across the committed appointment set minus what's been paid,
 * PLUS anything carried from earlier days. Reads `dayExpectedPayouts` so the cash
 * steer lines up with the floor and the payout view rather than only the sessions
 * already in/through the chair.
 *
 * Arrears belong in this total: they are owed now, and a steer that counted only
 * today's envelopes would read green on the morning after a deferral while the
 * drawer was still short.
 */
export function payoutsStillDueAud(studioId: StudioId, dayIso: string): number {
  return (
    dayExpectedPayouts(studioId, dayIso).reduce((sum, p) => sum + p.outstandingAud, 0) +
    studioArrearsAud(studioId, dayIso)
  );
}

/** Everything carried from earlier days across a studio, as at `uptoDayIso`. */
export function studioArrearsAud(studioId: StudioId, uptoDayIso: string): number {
  const brain = useBrainStore.getState();
  const today = dayKey(uptoDayIso);
  return Object.values(brain.payoutRecords)
    .filter((r) => r.studioId === studioId && r.status === 'deferred' && r.dayIso < today)
    .reduce((sum, r) => sum + r.amountDueAud, 0);
}

/** The day's cash ledger, or a zeroed default if none has been opened yet. */
export function cashLedgerFor(studioId: StudioId, dayIso: string): CashLedger {
  const brain = useBrainStore.getState();
  const id = cashLedgerId(studioId, dayIso);
  return (
    brain.cashLedgers[id] ?? {
      id,
      studioId,
      dayIso: dayKey(dayIso),
      openingFloatAud: 0,
      floatMovements: [],
      cashInAud: 0,
      payoutsAud: 0,
      floatDipped: false,
    }
  );
}

/**
 * Working cash = cash taken today − payouts handed out. The opening float is
 * DELIBERATELY excluded: it is a sealed emergency reserve, not spending money,
 * so the steering call (below) is honest about what today's takings actually
 * cover. The float is still visible on the ledger and can be broken for a
 * shortfall — but only as a flagged emergency.
 */
/** One work day an artist is still owed, oldest first. Read via `artistOutstanding`. */
export interface OutstandingDay {
  /** The work day the envelope is for. */
  dayIso: string;
  envelopeAud: number;
  /** Whole studio days between that work day and the read day. */
  ageDays: number;
  /** True once the day has aged past `ALERT_BANDS.payoutArrearsDays`. */
  escalated: boolean;
}

/** Whole studio days from `fromDayIso` to `toDayIso` (studio-local, order-safe). */
function dayGap(fromDayIso: string, toDayIso: string): number {
  const a = new Date(`${dayKey(fromDayIso)}T12:00:00+10:00`).getTime();
  const b = new Date(`${dayKey(toDayIso)}T12:00:00+10:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

/**
 * Every work day this artist is still owed as at `uptoDayIso`, oldest first.
 *
 * THE single arrears read: the payout card, the artist's own Earnings tab, the
 * cash cover maths and day close all call this, so what an artist is owed can
 * never differ between the screen that pays them and the screen they read.
 *
 * Arrears are always a WRITTEN record (`status: 'deferred'`, from `deferPayout` or
 * swept in by `closeDay`), never inferred by re-walking old days — an unpaid day
 * nobody decided about would otherwise be indistinguishable from a day that was
 * never worked.
 */
export function artistOutstanding(
  studioId: StudioId,
  artistId: string,
  uptoDayIso: string,
): OutstandingDay[] {
  const brain = useBrainStore.getState();
  const today = dayKey(uptoDayIso);
  return Object.values(brain.payoutRecords)
    .filter(
      (r) =>
        r.studioId === studioId &&
        r.artistId === artistId &&
        r.status === 'deferred' &&
        r.dayIso <= today,
    )
    .sort((a, b) => a.dayIso.localeCompare(b.dayIso))
    .map((r) => {
      const ageDays = dayGap(r.dayIso, today);
      return {
        dayIso: r.dayIso,
        envelopeAud: r.amountDueAud,
        ageDays,
        escalated: ageDays >= ALERT_BANDS.payoutArrearsDays,
      };
    });
}

/**
 * Total still owed to an artist from EARLIER days, as at `uptoDayIso` — strictly
 * before it (tv-2na.14.7, live D8). A same-day deferral is still today's envelope
 * (it stays outstanding on today's card), so counting it here too double-counted
 * it on the cover ladder ("Owed to here $1,056" for one $528 envelope). Matches
 * studioArrearsAud.
 */
export function artistArrearsAud(studioId: StudioId, artistId: string, uptoDayIso: string): number {
  const today = dayKey(uptoDayIso);
  return artistOutstanding(studioId, artistId, uptoDayIso)
    .filter((d) => d.dayIso < today)
    .reduce((sum, d) => sum + d.envelopeAud, 0);
}

export interface DeferPayoutInput {
  studioId: StudioId;
  artistId: string;
  dayIso: string;
  /** The manager who let the day roll (or the close that swept it). */
  decidedBy: string;
}

export interface DeferPayoutResult {
  ok: boolean;
  reason?: 'not-payable' | 'nothing-due' | 'no-artist' | 'already-paid';
  record?: PayoutRecord;
}

/**
 * Roll an artist's WHOLE envelope to a later day. No money moves and nothing is
 * part-paid: the artist is paid whole or not at all, so a day the takings cannot
 * cover waits, intact, until one can.
 *
 * The record is what makes the debt discoverable tomorrow — see `artistOutstanding`.
 */
export function deferPayout(input: DeferPayoutInput): DeferPayoutResult {
  const { studioId, artistId, dayIso, decidedBy } = input;
  const brain = useBrainStore.getState();
  const now = nowIso();

  const dp = dayPayouts(studioId, dayIso).find((p) => p.artistId === artistId);
  if (!dp) return { ok: false, reason: 'no-artist' };
  if (!dp.payable) return { ok: false, reason: 'not-payable' };
  if (dp.envelopeAud <= 0) return { ok: false, reason: 'nothing-due' };
  if (dp.record?.status === 'signed') return { ok: false, reason: 'already-paid' };

  const record: PayoutRecord = {
    id: payoutRecordId(studioId, artistId, dayIso),
    studioId,
    artistId,
    dayIso: dayKey(dayIso),
    arrangement: dp.arrangement,
    amountDueAud: dp.envelopeAud,
    amountPaidAud: 0,
    status: 'deferred',
    deferredAt: now,
    decidedBy,
  };
  brain.upsertPayoutRecord(record);
  brain.appendActivity({
    id: `act-payout-deferred-${record.id}`,
    type: 'payout-shortfall',
    studioId,
    artistId,
    amount: dp.envelopeAud,
    body: `${dp.artistName}'s ${aud(dp.envelopeAud)} envelope rolls to the next day the cash covers it in full (takings short; float sealed).`,
    timestamp: now,
  });
  eventBus.publish('payout:deferred', { studioId, artistId, dayIso: record.dayIso });
  return { ok: true, record };
}

/** What the drawer can settle for one artist right now, in whole days. */
export interface PayableBundle {
  /** Work days the cash covers, oldest first — arrears before today. */
  dayIsos: string[];
  /** Sum of those days' envelopes: what the hand-over is worth. */
  totalAud: number;
  /** Days it cannot cover whole, so they wait. */
  blockedDayIsos: string[];
  /** What the blocked days need beyond the cash on hand (0 when nothing is blocked). */
  shortAud: number;
  /** Everything owed to the artist as at the pay day, covered or not. */
  owedAud: number;
  /** The sealed float in the drawer on the pay day. */
  floatAud: number;
  /**
   * Whether breaking the float would pay the blocked days in full (14ym9bvbgg3):
   * there is a shortfall and the float holds at least that much. An empty or
   * too-small float is never offered — the days roll whole instead.
   */
  floatCovers: boolean;
}

/** Whether the pay day's float can fund a gap of `gapAud` (a positive shortfall). */
export function floatCoversGap(studioId: StudioId, payDayIso: string, gapAud: number): boolean {
  return gapAud > 0 && cashLedgerFor(studioId, payDayIso).openingFloatAud >= gapAud;
}

/**
 * Which whole days working cash can settle for an artist on `payDayIso`, oldest
 * first: arrears in date order, then today's envelope once it is payable.
 *
 * All-or-nothing applies PER WORK DAY (client, 30 Jul): $600 against $500 + $500
 * pays the older day and leaves the newer one, so the queue drains as far as the
 * cash honestly reaches instead of stalling on the total.
 */
export function payableBundle(
  studioId: StudioId,
  artistId: string,
  payDayIso: string,
): PayableBundle {
  const ledger = cashLedgerFor(studioId, payDayIso);
  const working = Math.max(0, ledger.cashInAud - ledger.payoutsAud);

  const candidates: Array<{ dayIso: string; envelopeAud: number }> = artistOutstanding(
    studioId,
    artistId,
    payDayIso,
  ).map((d) => ({ dayIso: d.dayIso, envelopeAud: d.envelopeAud }));

  // Today's envelope joins the queue last, and only once every session is through
  // the chair (an envelope still in the chair is not a debt yet).
  const todayKey = dayKey(payDayIso);
  const today = dayPayouts(studioId, payDayIso).find((p) => p.artistId === artistId);
  if (
    today &&
    today.payable &&
    today.outstandingAud > 0 &&
    !candidates.some((c) => c.dayIso === todayKey)
  ) {
    candidates.push({ dayIso: todayKey, envelopeAud: today.outstandingAud });
  }

  const dayIsos: string[] = [];
  const blockedDayIsos: string[] = [];
  let totalAud = 0;
  let shortAud = 0;
  let left = working;
  for (const c of candidates) {
    // Once a day is blocked every later day waits too: paying out of order would
    // leave an older debt behind while a newer one settles.
    if (blockedDayIsos.length === 0 && c.envelopeAud <= left) {
      dayIsos.push(c.dayIso);
      totalAud += c.envelopeAud;
      left -= c.envelopeAud;
    } else {
      blockedDayIsos.push(c.dayIso);
      shortAud += c.envelopeAud;
    }
  }
  // What the blocked days still need, over and above the cash left in the drawer.
  if (shortAud > 0) shortAud = Math.max(0, shortAud - left);

  return {
    dayIsos,
    totalAud,
    blockedDayIsos,
    shortAud,
    owedAud: candidates.reduce((sum, c) => sum + c.envelopeAud, 0),
    floatAud: ledger.openingFloatAud,
    floatCovers: shortAud > 0 && ledger.openingFloatAud >= shortAud,
  };
}

export interface SettlePayoutsInput {
  studioId: StudioId;
  artistId: string;
  /** The WORK days being settled (any order; stored oldest first). */
  dayIsos: string[];
  /** The day the cash actually moves — where the ledger is debited. */
  payDayIso: string;
  /** Name captured on screen (drawn or typed) — the artist confirming receipt. */
  signedByName: string;
  /** Break the sealed float to cover whatever working cash cannot. */
  useFloat?: boolean;
  /**
   * Record a hand-over the day's takings do not cover, without touching the float.
   * For callers that KNOW the money moved and are not deciding whether it should:
   * the seeded journeys (which never modelled a drawer) and the control console.
   * The payout screen never passes this — it narrows the bundle instead, via
   * `payableBundle`, so a manager cannot pay out cash the till does not hold.
   *
   * This is not a part-payment escape hatch: every day in the bundle is still paid
   * in full or not at all. Only the takings-funded portion hits the ledger, so
   * working cash never goes negative.
   */
  allowShortDrawer?: boolean;
}

export interface SettlePayoutsResult {
  ok: boolean;
  reason?: 'no-artist' | 'nothing-due' | 'not-covered' | 'not-payable' | 'float-short';
  records?: PayoutRecord[];
  /** Total handed over, and the run the records share. */
  paidAud?: number;
  paymentRunId?: string;
}

/** Deterministic id for one hand-over covering one or more work days. */
export function paymentRunId(studioId: StudioId, artistId: string, payDayIso: string): string {
  return `run-${studioId}-${artistId}-${dayKey(payDayIso)}`;
}

/**
 * Hand over one or more WHOLE work days as a single payment (the E2 sign-off,
 * now bundle-aware). THE one money write path for payouts.
 *
 * Every day in the bundle is paid in full or the call fails: `amountPaidAud`
 * always equals `amountDueAud`, so part payment is not representable. The days
 * keep their own `dayIso` ("payment for") and share `paidOnDayIso` + `paymentRunId`,
 * which is what lets one payment for Wednesday and Thursday be read back later.
 *
 * The cash is debited from the PAY day's ledger, never the work day's: paying
 * Wednesday's envelope on Thursday takes Thursday's cash, and the till has to
 * reconcile against the drawer the money actually left.
 */
export function settlePayouts(input: SettlePayoutsInput): SettlePayoutsResult {
  const { studioId, artistId, dayIsos, payDayIso, signedByName, useFloat, allowShortDrawer } = input;
  const brain = useBrainStore.getState();
  const now = nowIso();
  if (dayIsos.length === 0) return { ok: false, reason: 'nothing-due' };

  const payDay = dayKey(payDayIso);
  const days = [...new Set(dayIsos.map(dayKey))].sort();

  // Resolve every day to what it owes: an arrears record, or the live envelope
  // for the pay day itself. READ, DON'T RECOMPUTE — the amounts come from the
  // same derivations the screens read.
  const arrears = new Map(
    artistOutstanding(studioId, artistId, payDayIso).map((d) => [d.dayIso, d]),
  );
  const live = dayPayouts(studioId, payDayIso).find((p) => p.artistId === artistId);
  const artistName = live?.artistName ?? brain.artists[artistId]?.displayName ?? artistId;

  const legs: Array<{ dayIso: string; amountAud: number; arrangement: CommissionArrangement }> = [];
  for (const day of days) {
    const owed = arrears.get(day);
    if (owed) {
      const existing = brain.payoutRecords[payoutRecordId(studioId, artistId, day)];
      legs.push({
        dayIso: day,
        amountAud: owed.envelopeAud,
        arrangement: existing?.arrangement ?? live?.arrangement ?? arrangementForArtist(brain.artists[artistId]!),
      });
      continue;
    }
    if (day === payDay && live) {
      if (!live.payable) return { ok: false, reason: 'not-payable' };
      if (live.outstandingAud <= 0) return { ok: false, reason: 'nothing-due' };
      legs.push({ dayIso: day, amountAud: live.outstandingAud, arrangement: live.arrangement });
      continue;
    }
    // A carried day the manager already SENT (it left the deferred list when it
    // was sent): the artist's hold settles what was sent, whole.
    const sent = brain.payoutRecords[payoutRecordId(studioId, artistId, day)];
    if (sent?.status === 'sent' && sent.amountDueAud > 0) {
      legs.push({ dayIso: day, amountAud: sent.amountDueAud, arrangement: sent.arrangement });
      continue;
    }
    return { ok: false, reason: 'no-artist' };
  }

  const due = legs.reduce((sum, l) => sum + l.amountAud, 0);
  if (due <= 0) return { ok: false, reason: 'nothing-due' };

  // L5 (tv-mg91, D07-22): breaking the float is the MANAGER's decision, made when
  // they send the payment; the artist's hold then settles it. A sent record that
  // carries that decision funds the gap from the float at signing, and keeps the
  // manager as the one who decided it.
  const floatDecision = days
    .map((day) => brain.payoutRecords[payoutRecordId(studioId, artistId, day)])
    .find((r) => r?.status === 'sent' && r.shortfall === 'float-dip');
  const breakFloat = useFloat === true || floatDecision !== undefined;
  const decidedBy = floatDecision?.decidedBy ?? signedByName;

  const ledger = cashLedgerFor(studioId, payDayIso);
  const working = Math.max(0, ledger.cashInAud - ledger.payoutsAud);
  const fromTakings = Math.min(due, working);
  const gap = due - fromTakings;
  // Nothing part-pays. Without a float dip the drawer has to cover the bundle
  // whole; the caller narrows the bundle (see `payableBundle`) rather than
  // shaving the amount. `allowShortDrawer` is for callers recording a hand-over
  // that already happened off a drawer this prototype never modelled.
  if (gap > 0 && !breakFloat && !allowShortDrawer) return { ok: false, reason: 'not-covered' };
  // 14ym9bvbgg3 (a): the float funds a gap only when it holds the whole of it.
  // Paying past it would conjure cash the drawer never had (the movement clamps
  // at zero), so a float that can't cover the gap refuses like an uncovered drawer.
  if (gap > 0 && breakFloat && !allowShortDrawer && ledger.openingFloatAud < gap) {
    return { ok: false, reason: 'float-short' };
  }
  const fromFloat = breakFloat ? gap : 0;

  const runId = paymentRunId(studioId, artistId, payDayIso);
  const records = legs.map((leg): PayoutRecord => {
    const record: PayoutRecord = {
      id: payoutRecordId(studioId, artistId, leg.dayIso),
      studioId,
      artistId,
      dayIso: leg.dayIso,
      arrangement: leg.arrangement,
      amountDueAud: leg.amountAud,
      amountPaidAud: leg.amountAud,
      status: 'signed',
      paidOnDayIso: payDay,
      paymentRunId: runId,
      signedByName,
      signedAt: now,
      ...(fromFloat > 0 ? { shortfall: 'float-dip' as const, decidedBy } : {}),
    };
    brain.upsertPayoutRecord(record);
    return record;
  });

  // The ledger only ever counts takings-funded cash against working cash; the
  // float leg is recorded as an emergency movement instead, so working cash can
  // never go negative.
  brain.upsertCashLedger({
    ...cashLedgerFor(studioId, payDayIso),
    payoutsAud: ledger.payoutsAud + fromTakings,
  });
  if (fromFloat > 0) {
    recordFloatMovement(studioId, payDayIso, 'emergency-dip', -fromFloat, `Cover ${artistName}'s envelope`);
  }

  // One payment, one line in the feed — naming the days when it covers more than
  // the day it was paid on.
  const carriedLegs = legs.filter((l) => l.dayIso !== payDay);
  const forDays =
    carriedLegs.length > 0
      ? ` for ${legs.map((l) => format(new Date(`${l.dayIso}T12:00:00+10:00`), 'EEE d MMM')).join(' + ')}`
      : '';
  brain.appendActivity({
    id: `act-payout-${runId}`,
    type: fromFloat > 0 ? 'payout-shortfall' : 'payout-signed',
    studioId,
    artistId,
    amount: due,
    body:
      fromFloat > 0
        ? `${artistName} paid ${aud(due)} in full${forDays} · float broken for ${aud(fromFloat)} (emergency, logged).`
        : `${artistName} signed for ${aud(due)}${forDays}.`,
    timestamp: now,
  });

  eventBus.publish('payout:signed', {
    studioId,
    artistId,
    dayIso: payDay,
    dayIsos: days,
    status: 'signed',
    paymentRunId: runId,
  });
  return { ok: true, records, paidAud: due, paymentRunId: runId };
}

export interface SignPayoutInput {
  studioId: StudioId;
  artistId: string;
  dayIso: string;
  /** Name captured on screen (drawn or typed) — the artist confirming receipt. */
  signedByName: string;
  /**
   * Break the sealed float to pay the envelope in full when working cash can't,
   * and flag the ledger for E3/E4. There is no part-payment option: a day the
   * drawer cannot cover is deferred whole (`deferPayout`).
   */
  shortfall?: 'float-dip';
  /** See `SettlePayoutsInput.allowShortDrawer` — seeds and the console only. */
  allowShortDrawer?: boolean;
}

export interface SignPayoutResult {
  ok: boolean;
  reason?: 'not-payable' | 'nothing-due' | 'no-artist' | 'not-covered' | 'float-short';
  record?: PayoutRecord;
}

/**
 * Sign a SINGLE day's envelope as received — the thin wrapper the seeds and the
 * one-day screens use. All the work (and the only money write) is
 * `settlePayouts`; this exists so a caller with one day doesn't have to build a
 * bundle by hand.
 */
export function signPayout(input: SignPayoutInput): SignPayoutResult {
  const { studioId, artistId, dayIso, signedByName, shortfall, allowShortDrawer } = input;
  const dp = dayPayouts(studioId, dayIso).find((p) => p.artistId === artistId);
  if (!dp) return { ok: false, reason: 'no-artist' };
  if (!dp.payable) return { ok: false, reason: 'not-payable' };
  if (dp.outstandingAud <= 0) return { ok: false, reason: 'nothing-due' };

  const result = settlePayouts({
    studioId,
    artistId,
    dayIsos: [dayKey(dayIso)],
    payDayIso: dayIso,
    signedByName,
    ...(shortfall === 'float-dip' ? { useFloat: true } : {}),
    ...(allowShortDrawer ? { allowShortDrawer: true } : {}),
  });
  return {
    ok: result.ok,
    ...(result.reason ? { reason: result.reason } : {}),
    ...(result.records?.[0] ? { record: result.records[0] } : {}),
  };
}

export interface SendPayoutResult {
  ok: boolean;
  reason?: 'not-payable' | 'nothing-due' | 'no-artist' | 'float-short';
  record?: PayoutRecord;
  records?: PayoutRecord[];
  paymentRunId?: string;
}

/**
 * Manager action (D8, now bundle-aware): hand a payment to the artist to confirm
 * on their own device. Writes one 'sent' PayoutRecord per WORK day in the bundle,
 * sharing a `paymentRunId` and the intended `paidOnDayIso`, so the artist's tab
 * can show one hold-to-sign that names every day it covers.
 *
 * NO money moves here — `settlePayouts` on the artist side is still the single
 * money write path. Publishes payout:sent so the money UI can show "awaiting artist".
 */
export function sendPayouts(input: {
  studioId: StudioId;
  artistId: string;
  /** The work days being handed over (arrears first; defaults to the pay day). */
  dayIsos?: string[];
  payDayIso: string;
  /**
   * L5 (tv-mg91): the manager breaks the sealed float to cover what the drawer
   * can't. Recorded on the sent records as their decision (`shortfall:
   * 'float-dip'`, `decidedBy`); NO money moves until the artist holds to confirm,
   * when `settlePayouts` funds the gap from the float. The manager never signs.
   */
  breakFloatBy?: string;
}): SendPayoutResult {
  const { studioId, artistId, payDayIso } = input;
  const brain = useBrainStore.getState();
  const now = nowIso();
  const payDay = dayKey(payDayIso);
  const days = [...new Set((input.dayIsos ?? [payDay]).map(dayKey))].sort();

  const arrears = new Map(
    artistOutstanding(studioId, artistId, payDayIso).map((d) => [d.dayIso, d]),
  );
  const live = dayPayouts(studioId, payDayIso).find((p) => p.artistId === artistId);
  const artistName = live?.artistName ?? brain.artists[artistId]?.displayName ?? artistId;

  const legs: Array<{ dayIso: string; amountAud: number; arrangement: CommissionArrangement }> = [];
  for (const day of days) {
    const owed = arrears.get(day);
    if (owed) {
      const existing = brain.payoutRecords[payoutRecordId(studioId, artistId, day)];
      legs.push({
        dayIso: day,
        amountAud: owed.envelopeAud,
        arrangement: existing?.arrangement ?? live?.arrangement ?? arrangementForArtist(brain.artists[artistId]!),
      });
      continue;
    }
    if (day === payDay) {
      if (!live) return { ok: false, reason: 'no-artist' };
      if (!live.payable) return { ok: false, reason: 'not-payable' };
      if (live.outstandingAud <= 0) return { ok: false, reason: 'nothing-due' };
      legs.push({ dayIso: day, amountAud: live.outstandingAud, arrangement: live.arrangement });
      continue;
    }
    return { ok: false, reason: 'no-artist' };
  }
  if (legs.length === 0) return { ok: false, reason: 'nothing-due' };
  if (input.breakFloatBy) {
    // 14ym9bvbgg3 (a): only break a float that covers what the drawer can't.
    const due = legs.reduce((sum, l) => sum + l.amountAud, 0);
    const gap = due - Math.max(0, workingCashAud(studioId, payDayIso));
    if (gap > 0 && !floatCoversGap(studioId, payDayIso, gap)) return { ok: false, reason: 'float-short' };
  }

  const runId = paymentRunId(studioId, artistId, payDayIso);
  const records = legs.map((leg): PayoutRecord => {
    const record: PayoutRecord = {
      id: payoutRecordId(studioId, artistId, leg.dayIso),
      studioId,
      artistId,
      dayIso: leg.dayIso,
      arrangement: leg.arrangement,
      amountDueAud: leg.amountAud,
      amountPaidAud: 0,
      status: 'sent',
      paidOnDayIso: payDay,
      paymentRunId: runId,
      ...(input.breakFloatBy ? { shortfall: 'float-dip' as const, decidedBy: input.breakFloatBy } : {}),
    };
    brain.upsertPayoutRecord(record);
    return record;
  });

  const total = legs.reduce((sum, l) => sum + l.amountAud, 0);
  const forDays =
    legs.length > 1
      ? ` covering ${legs.map((l) => format(new Date(`${l.dayIso}T12:00:00+10:00`), 'EEE d MMM')).join(' + ')}`
      : '';
  brain.appendActivity({
    id: `act-payout-sent-${runId}`,
    type: 'payout-signed',
    studioId,
    artistId,
    amount: total,
    body: input.breakFloatBy
      ? `${artistName}'s ${aud(total)}${forDays} sent to their device to confirm · ${input.breakFloatBy} broke the float to cover it.`
      : `${artistName}'s ${aud(total)}${forDays} sent to their device to confirm.`,
    timestamp: now,
  });
  eventBus.publish('payout:sent', { studioId, artistId, dayIso: payDay, dayIsos: days, paymentRunId: runId });
  return { ok: true, record: records[0], records, paymentRunId: runId };
}

/** Single-day wrapper over `sendPayouts` for callers that hand over one day. */
export function sendPayout(input: { studioId: StudioId; artistId: string; dayIso: string }): SendPayoutResult {
  return sendPayouts({
    studioId: input.studioId,
    artistId: input.artistId,
    dayIsos: [dayKey(input.dayIso)],
    payDayIso: input.dayIso,
  });
}

/**
 * Move cash in or out of the drawer float and record it on the float ledger.
 * The float is NEVER overwritten by hand — every change is a movement (opening,
 * infusion, withdrawal, or an emergency dip) so the in/out is auditable. The
 * balance can't go negative; a withdrawal larger than the balance is clamped.
 * Returns the updated ledger.
 */
export function recordFloatMovement(
  studioId: StudioId,
  dayIso: string,
  kind: FloatMovement['kind'],
  amountAud: number,
  note?: string,
): CashLedger {
  const brain = useBrainStore.getState();
  const now = nowIso();
  const ledger = cashLedgerFor(studioId, dayIso);

  // Requested delta is signed; clamp so the float never dips below zero.
  const requested = Math.round(amountAud);
  const delta = Math.max(-ledger.openingFloatAud, requested);
  if (delta === 0) return ledger;

  const movement: FloatMovement = {
    id: `float-${ledger.id}-${ledger.floatMovements.length}`,
    at: now,
    kind,
    amountAud: delta,
    note,
  };
  const next: CashLedger = {
    ...ledger,
    openingFloatAud: ledger.openingFloatAud + delta,
    floatMovements: [...ledger.floatMovements, movement],
    // An emergency dip is the one movement that trips the E3/E4 flag.
    floatDipped: ledger.floatDipped || kind === 'emergency-dip',
  };
  brain.upsertCashLedger(next);

  const verb =
    kind === 'emergency-dip'
      ? `Float broken (emergency): ${aud(-delta)} out`
      : delta >= 0
        ? `Float infusion: ${aud(delta)} in`
        : `Float withdrawal: ${aud(-delta)} out`;
  brain.appendActivity({
    id: `act-float-${ledger.id}-${ledger.floatMovements.length}`,
    type: 'float-set',
    studioId,
    amount: delta,
    body: `${verb} · balance now ${aud(next.openingFloatAud)}.`,
    timestamp: now,
  });
  eventBus.publish('cash:float-movement', {
    studioId,
    dayIso: next.dayIso,
    kind,
    amountAud: delta,
    balanceAud: next.openingFloatAud,
  });
  return next;
}

/** One payment, and every work day it covered. Read via `payoutHistory`. */
export interface PayoutRun {
  /** Shared run id, or the record id for a lone legacy row without one. */
  id: string;
  /** The day the cash moved. Absent on a deferred day (nothing has moved yet). */
  paidOnDayIso?: string;
  artistId: string;
  artistName: string;
  status: PayoutRecord['status'];
  totalAud: number;
  /** The work days this payment settled, oldest first. */
  records: PayoutRecord[];
}

/**
 * The business-side register: payouts for a studio over a date range, grouped into
 * the payments that made them, newest payment first. A payment covering two work
 * days is ONE run with two records, which is what lets the History tab show one
 * paid date against two "payment for" dates.
 *
 * Deferred days have no payment yet, so each stands alone as its own row.
 */
export function payoutHistory(input: {
  studioId: StudioId;
  fromDayIso: string;
  toDayIso: string;
  artistId?: string;
}): PayoutRun[] {
  const brain = useBrainStore.getState();
  const from = dayKey(input.fromDayIso);
  const to = dayKey(input.toDayIso);

  const rows = Object.values(brain.payoutRecords).filter((r) => {
    if (r.studioId !== input.studioId) return false;
    if (input.artistId && r.artistId !== input.artistId) return false;
    if (r.status === 'due') return false;
    // A run is placed by the day the money moved; a day still waiting is placed
    // by the day it was worked.
    const placed = r.paidOnDayIso ?? r.dayIso;
    return placed >= from && placed <= to;
  });

  const byRun = new Map<string, PayoutRecord[]>();
  for (const r of rows) {
    const key = r.status === 'deferred' ? `deferred:${r.id}` : (r.paymentRunId ?? `single:${r.id}`);
    byRun.set(key, [...(byRun.get(key) ?? []), r]);
  }

  return [...byRun.entries()]
    .map(([id, records]): PayoutRun => {
      const ordered = [...records].sort((a, b) => a.dayIso.localeCompare(b.dayIso));
      const first = ordered[0]!;
      return {
        id,
        ...(first.paidOnDayIso ? { paidOnDayIso: first.paidOnDayIso } : {}),
        artistId: first.artistId,
        artistName: brain.artists[first.artistId]?.displayName ?? first.artistId,
        status: first.status,
        totalAud: ordered.reduce((sum, r) => sum + r.amountDueAud, 0),
        records: ordered,
      };
    })
    .sort((a, b) => {
      const byDay = (b.paidOnDayIso ?? b.records[0]!.dayIso).localeCompare(
        a.paidOnDayIso ?? a.records[0]!.dayIso,
      );
      return byDay !== 0 ? byDay : a.artistName.localeCompare(b.artistName);
    });
}

/**
 * An artist's payout records for a calendar month (their digital paper register),
 * newest day first. `monthIso` need only carry a year-month prefix (YYYY-MM).
 */
export function artistMonthlyPayouts(artistId: string, monthIso: string): PayoutRecord[] {
  const brain = useBrainStore.getState();
  const ym = monthIso.slice(0, 7);
  return Object.values(brain.payoutRecords)
    .filter((r) => r.artistId === artistId && r.dayIso.slice(0, 7) === ym)
    .sort((a, b) => b.dayIso.localeCompare(a.dayIso));
}

export function workingCashAud(studioId: StudioId, dayIso: string): number {
  const ledger = cashLedgerFor(studioId, dayIso);
  return ledger.cashInAud - ledger.payoutsAud;
}
